import * as Updates from 'expo-updates';
import React, { useEffect, useRef, useState } from 'react';
import {
  ActivityIndicator,
  AppState,
  AppStateStatus,
  Modal,
  Platform
} from 'react-native';
import styled from 'styled-components/native';

import AppManifest from '../common/AppManifest';
import Colors from '../constants/Colors';
import Snackbar from './Snackbar';
import Spacer from './Spacer';
import { H5, P } from './Typography';
import { log } from '../tools/LogHelper';
import { stringify } from 'flatted';
import AsyncStorage from '@react-native-async-storage/async-storage';

const StyledView = styled.View`
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  background: ${Colors.primaryPurple};
`;

const StyledImage = styled.Image`
  width: 59%;
  height: 64px;
  margin-bottom: 32px;
`;

/**
 * Checks if an update is available and prompt user to update
 */
const UpdateChecker: React.FC = () => {
  const mountRef = useRef(true);
  const initialUpdateCheckDoneRef = useRef(false);
  const appState = useRef(AppState.currentState);
  const [updating, setUpdating] = useState(false);
  const [debug, setDebug] = useState(JSON.stringify(Updates?.manifest || ''));
  const [error, setError] = useState<any>();

  useEffect(() => {
    if (Platform.OS === 'web') {
      return;
    }
    const fetchAndReloadTimeout = 45000;

    const checkForUpdates = async () => {
      try {
        const update = await Updates.checkForUpdateAsync();
        setDebug(JSON.stringify(update));
        if (update.isAvailable) {
          setUpdating(true);
          // fix for modal not rendering when UI is blocked
          setTimeout(async () => {
            const createTimeoutPromise = () =>
              new Promise<never>((_, reject) => {
                setTimeout(
                  () => reject(new Error('Update timed out')),
                  fetchAndReloadTimeout
                );
              });
            try {
              const results = await Promise.race([
                Updates.fetchUpdateAsync(),
                createTimeoutPromise()
              ]);
              setDebug(JSON.stringify(results));
              if (!results.isNew) {
                setUpdating(false);
                return;
              }
              await Promise.race([
                Updates.reloadAsync(),
                createTimeoutPromise()
              ]);
            } catch (e) {
              log('Error fetching/reloading update', stringify(e));
              setError(e);
              setDebug('Error: ' + JSON.stringify(e));
              setUpdating(false);
            }
          }, 500);
        }
      } catch (e) {
        // handle or log error
        log('Error updating', stringify(e));
        setError(e);
        setDebug('Error: ' + JSON.stringify(e));
        setTimeout(() => {
          setUpdating(false);
        }, 2000);
      }
    };

    const runInitialUpdateCheck = () => {
      if (initialUpdateCheckDoneRef.current) {
        return;
      }
      initialUpdateCheckDoneRef.current = true;
      checkForUpdates();
    };

    // check if app start first time
    (async () => {
      try {
        const firstTimeUpdateCheck = await AsyncStorage.getItem(
          'did-first-time-update-check'
        );
        if (!firstTimeUpdateCheck) {
          runInitialUpdateCheck();
          await AsyncStorage.setItem('did-first-time-update-check', 'true');
        }
      } catch (error) {
        log('Error on first-time update check', stringify(error));
      }
    })();

    // thereafter check if inactive/backgrond to foreground
    const handleAppStateChange = async (nextAppState: AppStateStatus) => {
      if (
        appState.current.match(/inactive|background/) &&
        nextAppState === 'active'
      ) {
        // console.log('App has come to the foreground!');
        await checkForUpdates();
      }
      // check for updates on first app start
      if (mountRef.current) {
        mountRef.current = false;
        runInitialUpdateCheck();
      }
      appState.current = nextAppState;
      log('AppState', appState.current);
    };
    const subscription = AppState.addEventListener(
      'change',
      handleAppStateChange
    );
    return () => {
      subscription.remove();
    };
  }, []);
  if (Platform.OS === 'web') {
    return null;
  }
  return (
    <>
      {updating && (
        <Modal animationType='slide' visible transparent={false}>
          <StyledView>
            <StyledImage
              source={require('../assets/header-logo.png')}
              resizeMode='contain'
            />
            <H5 color='light'>Updating App...</H5>
            <Spacer size={2} />
            <P color='light'>ENV: {AppManifest.env}</P>
            <P color='light'>Runtime: {AppManifest.runtimeVersion}</P>
            <P color='light'>
              Version: {AppManifest.version} (build: {AppManifest.buildNumber})
            </P>
            {/* <P color='light'>API: {environment.apiBaseUrl}</P> */}
            {Boolean(debug) && (
              <>
                <Spacer size={2} />
                <P color='light' style={{ color: '#fff' }}>
                  {debug}
                </P>
              </>
            )}
            <Spacer size={4} />
            <ActivityIndicator size='large' />
          </StyledView>
        </Modal>
      )}
      {error && (
        <Snackbar onPress={() => setError(undefined)}>Error updating</Snackbar>
      )}
    </>
  );
};

export default UpdateChecker;
